// Search-state for the users index route. The route in users.routes.tsx owns
// the validateSearch schema and stripSearchParams middleware; this hook is a
// thin, typed surface over TanStack's useSearch / useNavigate so consumers
// don't touch the router primitives directly. Tab / filter / sort / query
// changes reset page so previous-state pagination never bleeds into the new
// view.
//
// Users carries `status` and `roles` on top of the shared list params, so it
// models useProductRouterSearch rather than the createResourceRouterSearch
// factory, which only covers {q, page, perPage, sort, direction}.

// External
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

// Internal
import { DEFAULT_PAGE_SIZE } from '@components/DataTable/constants';
import type { ResourceSortDirection } from './resourceListPage';
import type { User } from '@api/users';

export interface UsersSearchParams {
  status: User['status'];
  q: string;
  /** Faceted role filter. Empty means unfiltered and leaves no trace in the URL. */
  roles: string[];
  page: number;
  perPage: number;
  sort: string;
  direction: ResourceSortDirection;
}

export const USERS_SEARCH_DEFAULTS: UsersSearchParams = {
  status: 'verified',
  q: '',
  roles: [],
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
  sort: 'name',
  direction: 'asc',
};

const USERS_ROUTE = '/users' as const;

const VALID_STATUSES: ReadonlySet<User['status']> = new Set([
  'verified',
  'unverified',
  'closed',
]);
const VALID_DIRECTIONS: ReadonlySet<'asc' | 'desc'> = new Set(['asc', 'desc']);

function toStringArray(raw: unknown): string[] {
  const arr = Array.isArray(raw)
    ? raw.map(String)
    : typeof raw === 'string' && raw.length > 0
      ? raw.split(',')
      : [];
  return arr.filter(Boolean);
}

// validateSearch handler — coerces unknown URL input into the typed shape.
export function parseUsersSearch(raw: Record<string, unknown>): UsersSearchParams {
  const status: User['status'] = VALID_STATUSES.has(raw.status as User['status'])
    ? (raw.status as User['status'])
    : USERS_SEARCH_DEFAULTS.status;
  const page = Number(raw.page);
  const perPage = Number(raw.perPage);
  const direction: ResourceSortDirection = VALID_DIRECTIONS.has(raw.direction as 'asc' | 'desc')
    ? (raw.direction as 'asc' | 'desc')
    : USERS_SEARCH_DEFAULTS.direction;
  return {
    status,
    q: typeof raw.q === 'string' ? raw.q : USERS_SEARCH_DEFAULTS.q,
    roles: toStringArray(raw.roles),
    page: Number.isFinite(page) && page > 0 ? page : USERS_SEARCH_DEFAULTS.page,
    perPage:
      Number.isFinite(perPage) && perPage > 0 ? perPage : USERS_SEARCH_DEFAULTS.perPage,
    // A string, not `|| default`: an explicit `?sort=` is the "no sort" state
    // and has to survive a popstate.
    sort: typeof raw.sort === 'string' ? raw.sort : USERS_SEARCH_DEFAULTS.sort,
    direction,
  };
}

export function useUsersRouterSearch() {
  const search = useSearch({ from: USERS_ROUTE });
  const tanNavigate = useNavigate({ from: USERS_ROUTE });

  const navigate = useCallback(
    (updates: Partial<UsersSearchParams>) => {
      tanNavigate({ search: (prev) => ({ ...prev, ...updates }), replace: true });
    },
    [tanNavigate],
  );

  return useMemo(
    () => ({
      search,
      navigate,
      setStatus: (status: User['status']) => navigate({ status, page: 1 }),
      setQuery: (q: string) => navigate({ q, page: 1 }),
      setRoles: (roles: string[]) => navigate({ roles, page: 1 }),
      setPage: (page: number) => navigate({ page }),
      setPerPage: (perPage: number) => navigate({ perPage, page: 1 }),
      setSort: (sort: string, direction: ResourceSortDirection) =>
        navigate({ sort, direction, page: 1 }),
    }),
    [search, navigate],
  );
}
